import { AsyncStatus, Queue } from 'lib-shared-types-api';

import { queuesApi } from 'apis/queues';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useCurrentUser } from 'utils/user';

export type UserQueues = {
  queues?: {
    [id: Queue['id']]: Queue['name'];
  };
  asyncStatus: AsyncStatus;
  restrictedQueuesEnabled: boolean;
};

export const useUserQueues = (): UserQueues => {
  const { user } = useCurrentUser();
  const { enableRestrictedQueues } = useFeatureFlag([
    'enable-restricted-queues',
  ]);

  const { queues, isLoading, isError } = queuesApi.useReadQueuesQuery(
    { agentId: user.id, limit: 500 },
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        queues: data?.data.queues.reduce(
          (queues, queue) => Object.assign(queues, { [queue.id]: queue.name }),
          {},
        ),
      }),
      skip: !enableRestrictedQueues,
    },
  );

  const asyncStatus: AsyncStatus =
    queues || !enableRestrictedQueues
      ? 'fulfilled'
      : isLoading
      ? 'pending'
      : isError
      ? 'rejected'
      : 'uninitialized';

  return {
    queues,
    asyncStatus,
    restrictedQueuesEnabled: enableRestrictedQueues,
  };
};

/**
 * Hook to check if the user is restricted from the queue
 *
 * @param queue The queue we check if the user is restricted from
 */
export const useIsRestrictedFromQueue = (
  queue: Pick<Queue, 'id' | 'is_restricted'> | null,
): boolean | undefined => {
  const userQueues = useUserQueues();

  return isRestrictedFromQueue(queue, userQueues);
};

/**
 * Checks if the user is restricted from the queue
 *
 * @param queue The queue we check if the user is restricted from
 * @param userQueues Object containing the queues that the user is a member of
 */
export const isRestrictedFromQueue = (
  queue: Pick<Queue, 'id' | 'is_restricted'> | null,
  userQueues: UserQueues,
): boolean | undefined => {
  if (!userQueues.restrictedQueuesEnabled) {
    return false;
  }
  if (
    userQueues.asyncStatus !== 'fulfilled' ||
    userQueues.queues === undefined
  ) {
    return undefined;
  }
  return queue?.is_restricted === true && userQueues.queues[queue.id] == null;
};
